/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, faMapMarkerAlt, faUserTie, faBriefcase, 
  faGlobeAmericas, faHandshake, faFileAlt, faShareAlt, 
  faCrown, faClipboardCheck, faUpload, faImage, faPhone,
  faEnvelope, faLink, faPlus, faTimes, faCheckCircle, 
  faInfoCircle, faCheck, faShieldAlt, faFileUpload, faSearch, faRocket
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { useAutoSave } from '../hooks/useAutoSave';
import '../styles/RecruiterRegistrationForm.css'; // Reusing recruiter styles for employer
import 'leaflet/dist/leaflet.css';
import globalSkillsData from '../data/global_skills.json';
import { STANDARD_INDUSTRIES } from '../utils/industryUtils';
import { ASSOCIATIONS, COMMUNITIES, ALL_OPTIONS, COUNTRIES as COMMUNITY_COUNTRIES } from '../data/communitiesAndAssociations';

// Import Leaflet directly (same as job seeker form)
import L from 'leaflet';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Community/Association Selector Component
const CommunityAssociationSelector = ({ value = [], onChange }) => {
  const [selectedItems, setSelectedItems] = useState(value);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState({ name: '', type: 'community', country: '' });
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedItems(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddFromDropdown = (option) => {
    const newItem = {
      name: option.name,
      type: option.type,
      country: ''
    };
    if (!selectedItems.some(item => item.name === option.name && item.type === option.type)) {
      const updated = [...selectedItems, newItem];
      setSelectedItems(updated);
      onChange(updated);
    }
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.name.trim()) {
      const newItem = {
        name: manualInput.name.trim(),
        type: manualInput.type,
        country: manualInput.country
      };
      if (!selectedItems.some(item => item.name === newItem.name && item.type === newItem.type)) {
        const updated = [...selectedItems, newItem];
        setSelectedItems(updated);
        onChange(updated);
        setManualInput({ name: '', type: 'community', country: '' });
        setShowManualForm(false);
      }
    }
  };

  const handleRemove = (index) => {
    const updated = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(updated);
    onChange(updated);
  };

  const handleUpdateCountry = (index, country) => {
    const updated = selectedItems.map((item, i) => 
      i === index ? { ...item, country } : item
    );
    setSelectedItems(updated);
    onChange(updated);
  };

  // Filter and group options
  const availableOptions = ALL_OPTIONS.filter(opt => 
    !selectedItems.some(item => item.name === opt.name && item.type === opt.type)
  );

  const filteredOptions = availableOptions.filter(opt =>
    opt.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const associations = filteredOptions.filter(opt => opt.type === 'association');
  const communities = filteredOptions.filter(opt => opt.type === 'community');

  return (
    <div style={{ width: '100%' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search communities/associations..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No results found
                  </div>
                ) : (
                  <>
                    {/* Associations Section */}
                    {associations.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Associations
                        </div>
                        {associations.map((opt, idx) => (
                          <div
                            key={`assoc-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Communities Section */}
                    {communities.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderTop: associations.length > 0 ? '2px solid #e9ecef' : 'none',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Communities
                        </div>
                        {communities.map((opt, idx) => (
                          <div
                            key={`comm-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: idx < communities.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowManualForm(!showManualForm)}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput.name}
              onChange={(e) => setManualInput({ ...manualInput, name: e.target.value })}
              placeholder="Enter community/association name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Type</label>
              <select
                value={manualInput.type}
                onChange={(e) => setManualInput({ ...manualInput, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="community">Community</option>
                <option value="association">Association</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Country</label>
              <select
                value={manualInput.country}
                onChange={(e) => setManualInput({ ...manualInput, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.name.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.name.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.name.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput({ name: '', type: 'community', country: '' });
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Selected Communities/Associations:
          </label>
          {selectedItems.map((item, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '8px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{item.name}</strong>
                  <span style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                    color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {item.type === 'association' ? 'Association' : 'Community'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px 8px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Country/Location:
                </label>
                <select
                  value={item.country || ''}
                  onChange={(e) => handleUpdateCountry(index, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">Select country</option>
                  {COMMUNITY_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Countries list - moved outside component for better performance
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
  "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "North Korea", "South Korea",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
  "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia",
  "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
  "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Norway",
  "Oman", "Pakistan", "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
  "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan",
  "Suriname", "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
  "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
  "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe"
];

const EmployerRegistrationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Auto-save configuration
  const AUTOSAVE_KEY = `recruiter_registration_${user?.userId || 'temp'}`;
  
  // Debug: Check what's in localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(AUTOSAVE_KEY);
    console.log('💾 localStorage check on mount:');
    console.log('   Key:', AUTOSAVE_KEY);
    console.log('   Has saved data:', !!savedData);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        console.log('   Saved formData:', parsed);
      } catch (e) {
        console.error('   Error parsing saved data:', e);
      }
    }
  }, [AUTOSAVE_KEY]);

  // Use auto-save hook (local draft; final submit uses handleCompleteProfile → form PUT)
  const {
    formData,
    setFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    forceSave,
    hasRecentSave,
    clearSavedData
  } = useAutoSave(
    {
      // Company Information
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      companyWebsite: '',
      companySize: '',
      yearFounded: '',
      industry: '',
      companyDescription: '',
      companyLogo: '',
      showLogoOnWebsite: true,
      
      // Company Location
      country: '',
      state: '',
      city: '',
      postalCode: '',
      address: '',
      latitude: '',
      longitude: '',
      
      // Recruiter Personal Details
      firstName: '',
      lastName: '',
      jobTitle: '',
      recruiterPhone: '',
      recruiterEmail: '',
      linkedinProfile: '',
      communitiesAssociations: [],
      
      // Terms
      agreeTerms: false,
      agreeDataProcessing: false,
      agreeMarketing: false,
      verifyInfo: false,
      
      // Other
      referralSource: '',
      additionalComments: '',
      participateInVolunteering: '',
      volunteeringDetails: '',
      hiringVolume: '',
      taxId: '',
      linkedinCompany: '',
      facebook: '',
      twitter: '',
      instagram: ''
    },
    AUTOSAVE_KEY,
    1000,
    null, // Backend callback will be handled via manual save functions
    true // Enable periodic auto-save to localStorage
  );

  const [industries, setIndustries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [additionalLinks, setAdditionalLinks] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);

  const [industryInput, setIndustryInput] = useState('');
  const [functionInput, setFunctionInput] = useState('');
  const [employmentTypeInput, setEmploymentTypeInput] = useState('');
  const [linkTypeInput, setLinkTypeInput] = useState('');
  const [linkUrlInput, setLinkUrlInput] = useState('');
  const [useMapAddress, setUseMapAddress] = useState(false);

  // Ensure "Security" appears in both industry and department dropdowns
  const industryOptions = useMemo(() => {
    const opts = [...STANDARD_INDUSTRIES];
    if (!opts.includes('Security')) opts.push('Security');
    return opts;
  }, []);

  const functionOptions = useMemo(() => {
    const opts = [...globalSkillsData.skills];
    if (!opts.includes('Security')) opts.push('Security');
    return opts;
  }, []);

  // Persist array-based fields across refreshes (since useAutoSave handles only formData)
  const ARRAYS_AUTOSAVE_KEY = useMemo(() => `${AUTOSAVE_KEY}_arrays`, [AUTOSAVE_KEY]);

  // Hydrate arrays on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(ARRAYS_AUTOSAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed?.industries)) setIndustries(parsed.industries);
        if (Array.isArray(parsed?.functions)) setFunctions(parsed.functions);
        if (Array.isArray(parsed?.additionalLinks)) setAdditionalLinks(parsed.additionalLinks);
        if (Array.isArray(parsed?.employmentTypes)) setEmploymentTypes(parsed.employmentTypes);
        if (Array.isArray(parsed?.additionalServices)) setAdditionalServices(parsed.additionalServices);
      }
    } catch (_) { /* ignore */ }
  }, [ARRAYS_AUTOSAVE_KEY]);

  // Save arrays whenever they change
  useEffect(() => {
    const payload = {
      industries,
      functions,
      additionalLinks,
      employmentTypes,
      additionalServices
    };
    try {
      localStorage.setItem(ARRAYS_AUTOSAVE_KEY, JSON.stringify(payload));
    } catch (_) { /* ignore quota errors */ }
  }, [industries, functions, additionalLinks, employmentTypes, additionalServices, ARRAYS_AUTOSAVE_KEY]);

  // Update logo preview when formData.companyLogo changes (from autosave or backend)
  useEffect(() => {
    if (formData.companyLogo && formData.companyLogo !== logoPreview) {
      setLogoPreview(formData.companyLogo);
    }
  }, [formData.companyLogo]);

  // Define setMarker function before useEffect that uses it
  const setMarker = useCallback((lat, lng, forceUpdateAddress = false) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }
    
    const marker = L.marker([lat, lng], {
      draggable: true
    }).addTo(map);

    markerRef.current = marker;

    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    marker.on('dragend', function(e) {
      const position = marker.getLatLng();
      setFormData(prev => ({
        ...prev,
        latitude: position.lat,
        longitude: position.lng
      }));
      
      // Only reverse geocode if user wants to use map address
      if (useMapAddress) {
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.lat}&lon=${position.lng}`)
          .then(response => response.json())
          .then(data => {
            if (data.display_name) {
              setFormData(prev => ({
                ...prev,
                address: data.display_name
              }));
            }
          })
          .catch(error => {
            console.log('Reverse geocoding failed:', error);
          });
      }
    });

    // Only reverse geocode if user wants to use map address or it's forced (initial load)
    if (useMapAddress || forceUpdateAddress) {
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
        .then(response => response.json())
        .then(data => {
          if (data.display_name) {
            setFormData(prev => ({
              ...prev,
              address: data.display_name
            }));
          }
        })
        .catch(error => {
          console.log('Reverse geocoding failed:', error);
        });
    }
  }, [useMapAddress]);

  // Initialize map (same simple approach as job seeker form)
  useEffect(() => {
    const initMap = () => {
      if (mapRef.current && !mapInstanceRef.current) {
        // Default center (Nairobi, Kenya)
        const map = L.map(mapRef.current).setView([-1.286389, 36.817223], 12);
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;

        // Add click event to map
        map.on('click', function(e) {
          setMarker(e.latlng.lat, e.latlng.lng, false);
        });

        // Try to get user's current location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(function(position) {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            map.setView([lat, lng], 13);
            // Don't force update address on initial geolocation
            setMarker(lat, lng, false);
          });
        }

        setMapLoaded(true);
      }
    };

    const timer = setTimeout(initMap, 100);
    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [setMarker]);

  const updateProgress = useCallback(() => {
    // Count required fields (excluding terms checkboxes for base calculation)
    const requiredFields = [
      formData.companyName, formData.companyEmail, formData.companyPhone,
      formData.companySize, formData.yearFounded,
      formData.companyDescription, formData.country, formData.state,
      formData.city, formData.address, formData.firstName, formData.lastName,
      formData.jobTitle, formData.recruiterPhone, formData.recruiterEmail
    ];

    const filledFields = requiredFields.filter(field => {
      return field && field.toString().trim() !== '';
    }).length;

    // Count array fields
    let arrayFieldsCount = 0;
    if (industries.length > 0) arrayFieldsCount++;
    if (functions.length > 0) arrayFieldsCount++;
    if (employmentTypes.length > 0) arrayFieldsCount++;
    
    // Count terms checkboxes
    let termsCount = 0;
    if (formData.agreeTerms) termsCount++;
    if (formData.agreeDataProcessing) termsCount++;
    if (formData.verifyInfo) termsCount++;

    // Total: 16 required fields + 3 arrays + 3 terms = 22 total
    const totalRequired = 22;
    const totalFilled = filledFields + arrayFieldsCount + termsCount;

    const progressPercent = Math.min((totalFilled / totalRequired) * 100, 100);
    setProgress(progressPercent);
  }, [formData, industries, functions, employmentTypes]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'additionalServices[]') {
        const updatedServices = checked
          ? [...additionalServices, value]
          : additionalServices.filter(s => s !== value);
        setAdditionalServices(updatedServices);
      } else if (name === 'useMapAddress') {
        setUseMapAddress(checked);
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      // Log when important fields are updated
      if (['companyName', 'firstName', 'lastName', 'companyEmail'].includes(name)) {
        console.log(`📝 Field updated: ${name} = "${value}"`);
      }
      
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: value
        };
        return updated;
      });
    }
    
    updateProgress();
  }, [employmentTypes, additionalServices, updateProgress]);

  const handleLogoChange = (e) => {
    console.log('🖼️ handleLogoChange triggered');
    const file = e.target.files[0];
    console.log('   Selected file:', file);
    
    if (file) {
      console.log('   File name:', file.name);
      console.log('   File size:', file.size, 'bytes');
      console.log('   File type:', file.type);
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      if (!file.type.match(/^image\/(png|jpg|jpeg)$/i)) {
        alert('Only PNG and JPG images are allowed');
        return;
      }
      
      console.log('   ✅ File validation passed, reading file...');
      const reader = new FileReader();
      reader.onload = (e) => {
        const logoData = e.target.result;
        console.log('   ✅ Logo data loaded, length:', logoData.length);
        setLogoPreview(logoData);
        // Save logo data to formData
        setFormData(prev => ({
          ...prev,
          companyLogo: logoData
        }));
        console.log('   ✅ Logo saved to formData');
      };
      reader.readAsDataURL(file);
    } else {
      console.log('   ❌ No file selected');
    }
  };

  const addIndustry = () => {
    if (industryInput && !industries.includes(industryInput)) {
      setIndustries([...industries, industryInput]);
      setIndustryInput('');
      updateProgress();
    }
  };

  const removeIndustry = (index) => {
    setIndustries(industries.filter((_, i) => i !== index));
    updateProgress();
  };

  const addEmploymentType = () => {
    if (employmentTypeInput && !employmentTypes.includes(employmentTypeInput)) {
      setEmploymentTypes([...employmentTypes, employmentTypeInput]);
      setEmploymentTypeInput('');
      updateProgress();
    }
  };

  const removeEmploymentType = (index) => {
    setEmploymentTypes(employmentTypes.filter((_, i) => i !== index));
    updateProgress();
  };

  const addFunction = () => {
    if (functionInput && !functions.includes(functionInput)) {
      setFunctions([...functions, functionInput]);
      setFunctionInput('');
      updateProgress();
    }
  };

  const removeFunction = (index) => {
    setFunctions(functions.filter((_, i) => i !== index));
    updateProgress();
  };

  const addLink = () => {
    if (!linkTypeInput.trim()) {
      alert('Please enter a link type');
      return;
    }
    
    if (!linkUrlInput.trim()) {
      alert('Please enter a URL');
      return;
    }

    try {
      new URL(linkUrlInput);
    } catch (e) {
      alert('Please enter a valid URL (including https://)');
      return;
    }

    setAdditionalLinks([...additionalLinks, { type: linkTypeInput, url: linkUrlInput }]);
    setLinkTypeInput('');
    setLinkUrlInput('');
  };

  const removeLink = (index) => {
    setAdditionalLinks(additionalLinks.filter((_, i) => i !== index));
  };

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  // Complete profile: flush local auto-save then submit the main form (same flow as intern/job seeker headers)
  const handleCompleteProfile = async () => {
    if (isLoading || isSaving) return;
    try {
      await forceSave();
    } catch (e) {
      console.error('Flush before complete profile failed:', e);
    }
    const formEl = document.getElementById('recruiterForm');
    if (formEl) {
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        formEl.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  };

  const handleGoToDashboard = async () => {
    try {
      await forceSave();
    } catch (e) {
      console.error('Failed to flush form before dashboard:', e);
    }
    navigate('/employer-dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Pre-submission validation - check if required text fields are filled
    console.log('🔍 PRE-SUBMIT CHECK - formData:', formData);
    
    const requiredTextFields = {
      'Company Name': formData.companyName,
      'Company Email': formData.companyEmail,
      'Company Phone': formData.companyPhone,
      'Company Size': formData.companySize,
      'Year Founded': formData.yearFounded,
      'Company Description': formData.companyDescription,
      'Country': formData.country,
      'State': formData.state,
      'City': formData.city,
      'Address': formData.address,
      'First Name': formData.firstName,
      'Last Name': formData.lastName,
      'Job Title': formData.jobTitle,
      'Phone Number': formData.recruiterPhone,
      'Email': formData.recruiterEmail
    };
    
    const emptyFields = Object.entries(requiredTextFields)
      .filter(([key, value]) => !value || value.trim() === '')
      .map(([key]) => key);
    
    if (emptyFields.length > 0) {
      alert(`Please fill in the following required fields:\n\n${emptyFields.join('\n')}`);
      setIsLoading(false);
      return;
    }
    
    // Validation for arrays - Industry is validated here since it's a multi-select
    if (industries.length === 0) {
      alert('Please add at least one industry you recruit for');
      return;
    }

    if (functions.length === 0) {
      alert('Please add at least one job function');
      return;
    }

    setIsLoading(true);
    setSubmitError('');

    try {
      console.log('🔍 DEBUG - formData state:', formData);
      console.log('🔍 DEBUG - industries state:', industries);
      console.log('🔍 DEBUG - functions state:', functions);
      
      const submitData = {
        ...formData,
        // Set industry to first industry in array for backend compatibility
        industry: industries.length > 0 ? industries[0] : '',
        industries,
        functions,
        additionalLinks,
        employmentTypes,
        additionalServices
      };

      // Debug: Log the data being submitted
      console.log('📤 Submitting employer data:');
      console.log('   - companyName:', submitData.companyName);
      console.log('   - companyEmail:', submitData.companyEmail);
      console.log('   - companyPhone:', submitData.companyPhone);
      console.log('   - firstName:', submitData.firstName);
      console.log('   - lastName:', submitData.lastName);
      console.log('   - city:', submitData.city);
      console.log('   - country:', submitData.country);
      console.log('   - industries:', submitData.industries);
      console.log('   - functions:', submitData.functions);
      console.log('   - employmentTypes:', submitData.employmentTypes);
      console.log('Full data object:', submitData);
      
      // Final verification before sending
      if (!submitData.companyName || !submitData.firstName) {
        console.error('❌ CRITICAL: Key fields are missing!');
        console.error('   formData object:', formData);
        alert('Error: Form data is incomplete. Please check all required fields and try again.');
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      // Prepare final payload with profileCompleted flag
      const finalPayload = {
        ...submitData,
        profileCompleted: true
      };
      
      console.log('🚀 Final payload to send:', finalPayload);
      
      const response = await fetch(buildApiUrl('/api/recruiters/profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(finalPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Registration failed:', errorData);
        throw new Error(errorData.message || 'Registration failed');
      }

      const result = await response.json();
      console.log('✅ Registration successful:', result);
      
      // Verify the saved data
      if (result.user) {
        console.log('📊 Verification - Data returned from server:');
        console.log('   - companyName:', result.user.companyName);
        console.log('   - firstName:', result.user.firstName);
        console.log('   - industries:', result.user.industries);
      }
      
      // Clear saved draft data after successful submission
      clearSavedData();
      
      alert('Profile created successfully!');
      navigate('/employer-dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.message || 'Failed to submit registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recruiter-details-comprehensive">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .recruiter-details-comprehensive {
          max-width: 100% !important;
        }
        .recruiter-header {
          width: 100% !important;
          max-width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .recruiter-header .header-container {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          padding: 12px 20px 12px 10px !important;
          max-width: 100% !important;
          margin: 0 !important;
          width: 100% !important;
        }
        .recruiter-header .header-left {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-right: auto !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          flex: 0 0 auto !important;
          order: 1 !important;
        }
        .recruiter-header .header-left .logo-icon {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-left .logo-text {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-right {
          margin-left: auto !important;
          flex: 0 0 auto !important;
          order: 2 !important;
        }
      `}</style>
      
      {/* Header */}
      <header className="recruiter-header">
        <div className="header-container">
          <div className="header-left" onClick={() => navigate('/employer-dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="logo-icon">
              <img src="/AK_logo.png" alt="AksharJobs Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </div>
            <div className="logo-text">
              <h3 className="logo-title" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>AksharJobs</h3>
              <p className="logo-subtitle" style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>Employer Registration</p>
            </div>
          </div>
          <div className="header-right">
            <div className="header-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="auto-save-status" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                {isSaving && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f39c12' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #f39c12', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#27ae60' }}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Auto-saved {lastSaveTime && `at ${lastSaveTime.toLocaleTimeString()}`}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e74c3c' }}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Save failed - data preserved locally</span>
                  </div>
                )}
                {saveStatus === 'loaded' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Previous data restored</span>
                  </div>
                )}
                {!isSaving && !saveStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6c757d' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Auto-save enabled (every 2 minutes)</span>
                  </div>
                )}
              </div>
              <button
                className="complete-profile-btn"
                type="button"
                onClick={handleCompleteProfile}
                disabled={isLoading || isSaving}
                style={{
                  background: (isLoading || isSaving) ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isLoading || isSaving) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && !isSaving) e.target.style.background = '#2980b9';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading && !isSaving) e.target.style.background = '#3498db';
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} /> {(isLoading || isSaving) ? 'Submitting...' : 'Complete profile'}
              </button>
              <button
                className="go-to-dashboard-btn"
                type="button"
                onClick={handleGoToDashboard}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#6b7280';
                }}
              >
                <FontAwesomeIcon icon={faRocket} /> {isSaving ? 'Saving...' : 'Go to dashboard'}
              </button>
              <button
                className="clear-form-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved form data? This cannot be undone.')) {
                    clearSavedData();
                    window.location.reload();
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #e74c3c',
                  color: '#e74c3c',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#e74c3c';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#e74c3c';
                }}
              >
                Clear Form Data
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="recruiter-main">
        <div className="recruiter-container">
          <div className="recruiter-form-card">
            {/* Main Form */}
            <div className="recruiter-form-container">
              <form id="recruiterForm" onSubmit={handleSubmit}>
                {/* Enhanced Progress Section */}
                <div className="progress-section-comprehensive" style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '30px',
                  color: '#000000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '2px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="progress-header-comprehensive" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#000000' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px', color: '#0d9488' }} />
                      Registration Progress
                    </h2>
                    <div style={{
                      background: '#f5f5f5',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }}>
                      {Math.round(progress)}%
                    </div>
                  </div>
                  
                  <div className="progress-bar-comprehensive" style={{
                    background: '#f5f5f5',
                    height: '12px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '15px',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    <div 
                      className="progress-fill-comprehensive" 
                      style={{ 
                        width: `${progress}%`,
                        height: '100%',
                        background: '#0d9488',
                        borderRadius: '6px',
                        transition: 'width 0.5s ease-in-out',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        width: '20px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 100%)',
                        borderRadius: '0 6px 6px 0'
                      }}></div>
                    </div>
                  </div>

                  {/* Progress Status Message */}
                  <div style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    textAlign: 'center',
                    marginBottom: '10px',
                    color: '#000000'
                  }}>
                    {Math.round(progress) === 100 ? (
                      <span style={{ color: '#0d9488', fontWeight: '600' }}>
                        🎉 Profile Complete! Ready to submit.
                      </span>
                    ) : Math.round(progress) >= 80 ? (
                      <span style={{ color: '#000000' }}>
                        Almost there! Just a few more fields to complete.
                      </span>
                    ) : Math.round(progress) >= 50 ? (
                      <span style={{ color: '#000000' }}>
                        Good progress! Keep filling out the form.
                      </span>
                    ) : (
                      <span style={{ color: '#000000' }}>
                        Let's get started! Fill out your company information.
                      </span>
                    )}
                  </div>

                  {/* Quick Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '10px',
                    fontSize: '12px',
                    marginBottom: '15px',
                    color: '#000000'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>7</div>
                      <div style={{ color: '#000000' }}>Sections</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>35+</div>
                      <div style={{ color: '#000000' }}>Fields</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>Auto-save</div>
                      <div style={{ color: '#000000' }}>Enabled</div>
                    </div>
                  </div>

                  {/* Section Completion Checklist */}
                  {Math.round(progress) < 100 && (
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#000000' }}>
                        Complete these sections:
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '6px'
                      }}>
                        {[
                          { key: 'company', name: 'Company Info', icon: '🏢', completed: formData.companyName && formData.companyEmail && formData.companySize },
                          { key: 'location', name: 'Location', icon: '📍', completed: formData.country && formData.city && formData.address },
                          { key: 'personal', name: 'Personal Details', icon: '👤', completed: formData.firstName && formData.lastName && formData.jobTitle },
                          { key: 'specialization', name: 'Specialization', icon: '💼', completed: industries.length > 0 && functions.length > 0 },
                          { key: 'services', name: 'Services', icon: '🤝', completed: employmentTypes.length > 0 },
                          { key: 'social', name: 'Social Media', icon: '🔗', completed: formData.linkedinCompany || formData.companyWebsite },
                          { key: 'terms', name: 'Terms', icon: '✅', completed: formData.agreeTerms && formData.agreeDataProcessing && formData.verifyInfo }
                        ].map(section => (
                          <div key={section.key} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: section.completed ? 0.6 : 1
                          }}>
                            <span style={{
                              color: section.completed ? '#0d9488' : '#000000',
                              fontSize: '14px'
                            }}>
                              {section.completed ? '✅' : section.icon}
                            </span>
                            <span style={{
                              textDecoration: section.completed ? 'line-through' : 'none',
                              opacity: section.completed ? 0.7 : 1,
                              color: '#000000'
                            }}>
                              {section.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Company Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faBuilding} />
                    Company Information
                  </h2>

                  {logoPreview && (
                    <div className="profile-photo-preview-comprehensive" style={{ display: 'block' }}>
                      <img src={logoPreview} alt="Company Logo" />
                    </div>
                  )}

                  <div className="form-group-comprehensive">
                    <label className="file-upload-comprehensive">
                      <FontAwesomeIcon icon={faImage} />
                      <div className="file-upload-text-comprehensive">
                        <strong>Upload Company Logo</strong>
                        <p>Click to browse or drag and drop (PNG, JPG, max 5MB)</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleLogoChange} />
                    </label>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Company Name <span className="required">*</span></label>
                    <input 
                      type="text" 
                      name="companyName" 
                      required 
                      placeholder="Enter your company name"
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Company Email <span className="required">*</span></label>
                      <input 
                        type="email" 
                        name="companyEmail" 
                        required 
                        placeholder="contact@company.com"
                        value={formData.companyEmail}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Company Phone <span className="required">*</span></label>
                      <input 
                        type="tel" 
                        name="companyPhone" 
                        required 
                        placeholder="+254 700 000 000"
                        value={formData.companyPhone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Company Website</label>
                    <input 
                      type="text" 
                      name="companyWebsite" 
                      placeholder="https://www.yourcompany.com or yourcompany.co.ke"
                      value={formData.companyWebsite}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Company Size <span className="required">*</span></label>
                      <select 
                        name="companySize" 
                        required
                        value={formData.companySize}
                        onChange={handleInputChange}
                      >
                        <option value="">Select company size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="501-1000">501-1000 employees</option>
                        <option value="1001-5000">1001-5000 employees</option>
                        <option value="5001+">5001+ employees</option>
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Year Founded <span className="required">*</span></label>
                      <input 
                        type="number" 
                        name="yearFounded" 
                        required 
                        min="1800" 
                        max="2025" 
                        placeholder="2015"
                        value={formData.yearFounded}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Industry/Sector <span className="required">*</span></label>
                    <div className="industry-multiselect-container">
                      <select 
                        name="industrySelect"
                        value={industryInput}
                        onChange={(e) => setIndustryInput(e.target.value)}
                        className="industry-select-input"
                      >
                        <option value="">Select industries</option>
                        {industryOptions.filter(industry => !industries.includes(industry)).map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                      <button 
                        type="button" 
                        className="add-btn" 
                        onClick={addIndustry}
                        disabled={!industryInput}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add
                      </button>
                    </div>
                    {industries.length > 0 && (
                      <div className="tags-container-comprehensive">
                        {industries.map((industry, index) => (
                          <span key={index} className="tag-comprehensive">
                            {industry}
                            <span className="remove" onClick={() => removeIndustry(index)}>
                              <FontAwesomeIcon icon={faTimes} />
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                    {industries.length === 0 && (
                      <p className="field-hint">Please add at least one industry</p>
                    )}
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Company Description <span className="required">*</span></label>
                    <textarea 
                      name="companyDescription" 
                      required 
                      placeholder="Describe your company, its mission, vision, and what makes it a great place to work. (Minimum 150 characters)"
                      value={formData.companyDescription}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Company Location Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    Company Location
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Country <span className="required">*</span></label>
                      <select 
                        name="country" 
                        required
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="">Select your country</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>State/Province <span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="state" 
                        required 
                        placeholder="e.g., Nairobi"
                        value={formData.state}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>City <span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="city" 
                        required 
                        placeholder="e.g., Nairobi"
                        value={formData.city}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Postal/ZIP Code</label>
                      <input 
                        type="text" 
                        name="postalCode" 
                        placeholder="Enter postal code"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Full Address <span className="required">*</span></label>
                    <input 
                      type="text" 
                      name="address" 
                      required 
                      placeholder="Street address, building name, suite number"
                      value={formData.address}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Pin Your Office Location on Map (Optional)</label>
                    <div className="info-badge">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> Click on the map to mark your office location
                    </div>

                    <div ref={mapRef} className="map-container" style={{ height: '300px', width: '100%', marginTop: '10px' }}>
                      {!mapLoaded && (
                        <div style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)', 
                          color: '#666',
                          fontSize: '14px'
                        }}>
                          Loading map...
                        </div>
                      )}
                    </div>
                    {mapLoaded && (
                      <div className="coordinates-display">
                        <strong>Selected Coordinates:</strong> 
                        {formData.latitude && formData.longitude 
                          ? `Lat: ${parseFloat(formData.latitude).toFixed(6)}, Lng: ${parseFloat(formData.longitude).toFixed(6)}`
                          : ' Click on the map to select'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Employer Personal Details Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUserTie} />
                    Employer Personal Details
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>First Name <span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="firstName" 
                        required 
                        placeholder="Enter your first name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Last Name <span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="lastName" 
                        required 
                        placeholder="Enter your last name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Job Title <span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="jobTitle" 
                        required 
                        placeholder="e.g., CEO, Founder, HR Director, Business Owner"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Direct Phone Number <span className="required">*</span></label>
                      <input 
                        type="tel" 
                        name="recruiterPhone" 
                        required 
                        placeholder="+254 700 000 000"
                        value={formData.recruiterPhone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Professional Email <span className="required">*</span></label>
                    <input 
                      type="email" 
                      name="recruiterEmail" 
                      required 
                      placeholder="your.name@company.com"
                      value={formData.recruiterEmail}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>LinkedIn Profile</label>
                    <input 
                      type="url" 
                      name="linkedinProfile" 
                      placeholder="https://www.linkedin.com/in/yourprofile"
                      value={formData.linkedinProfile}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group-comprehensive" style={{ width: '100%' }}>
                    <label>Community / Association</label>
                    <CommunityAssociationSelector
                      value={formData.communitiesAssociations || []}
                      onChange={(items) => setFormData(prev => ({ ...prev, communitiesAssociations: items }))}
                    />
                  </div>
                </div>

                {/* Recruitment Specialization Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faBriefcase} />
                    Recruitment Specialization
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>Job Functions You Recruit For <span className="required">*</span></label>
                    <div className="info-badge">
                      <FontAwesomeIcon icon={faInfoCircle} /> Type or select from dropdown
                    </div>
                    <div className="skills-input-container">
                      <input 
                        type="text" 
                        list="jobFunctionsList"
                        placeholder="Type or select job function"
                        value={functionInput}
                        onChange={(e) => setFunctionInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFunction())}
                      />
                      <datalist id="jobFunctionsList">
                        {functionOptions.map((skill, index) => (
                          <option key={index} value={skill} />
                        ))}
                      </datalist>
                      <button type="button" className="add-btn" onClick={addFunction}>
                        <FontAwesomeIcon icon={faPlus} /> Add
                      </button>
                    </div>
                    <div className="tags-container">
                      {functions.map((func, index) => (
                        <div key={index} className="tag">
                          {func}
                          <span className="remove" onClick={() => removeFunction(index)}>
                            <FontAwesomeIcon icon={faTimes} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Average Hiring Volume (Monthly)</label>
                    <select 
                      name="hiringVolume"
                      value={formData.hiringVolume}
                      onChange={handleInputChange}
                    >
                      <option value="">Select hiring volume</option>
                      <option value="1-5">1-5 positions</option>
                      <option value="6-10">6-10 positions</option>
                      <option value="11-20">11-20 positions</option>
                      <option value="21-50">21-50 positions</option>
                      <option value="51+">51+ positions</option>
                    </select>
                  </div>
                </div>


                {/* Services & Offerings Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faHandshake} />
                    Services & Offerings
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>Types of Employment You Offer <span className="required">*</span></label>
                    <div className="industry-multiselect-container">
                      <select 
                        name="employmentTypeSelect"
                        value={employmentTypeInput}
                        onChange={(e) => setEmploymentTypeInput(e.target.value)}
                        className="industry-select-input"
                      >
                        <option value="">Select employment types</option>
                        {!employmentTypes.includes('full-time') && (
                          <option value="full-time">Full-time Positions</option>
                        )}
                        {!employmentTypes.includes('part-time') && (
                          <option value="part-time">Part-time Positions</option>
                        )}
                        {!employmentTypes.includes('contract') && (
                          <option value="contract">Contract/Temporary</option>
                        )}
                        {!employmentTypes.includes('freelance') && (
                          <option value="freelance">Freelance/Project-based</option>
                        )}
                        {!employmentTypes.includes('internship') && (
                          <option value="internship">Internships</option>
                        )}
                      </select>
                      <button 
                        type="button" 
                        className="add-btn" 
                        onClick={addEmploymentType}
                        disabled={!employmentTypeInput}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add
                      </button>
                    </div>
                    {employmentTypes.length > 0 && (
                      <div className="tags-container-comprehensive">
                        {employmentTypes.map((type, index) => (
                          <span key={index} className="tag-comprehensive">
                            {type === 'full-time' ? 'Full-time Positions' :
                             type === 'part-time' ? 'Part-time Positions' :
                             type === 'contract' ? 'Contract/Temporary' :
                             type === 'freelance' ? 'Freelance/Project-based' :
                             type === 'internship' ? 'Internships' : type}
                            <span className="remove" onClick={() => removeEmploymentType(index)}>
                              <FontAwesomeIcon icon={faTimes} />
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                    {employmentTypes.length === 0 && (
                      <p className="field-hint">Please add at least one employment type</p>
                    )}
                  </div>
                </div>

                {/* Social Media Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faShareAlt} />
                    Social Media & Online Presence
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Company LinkedIn Page</label>
                      <input 
                        type="url" 
                        name="linkedinCompany" 
                        placeholder="https://www.linkedin.com/company/yourcompany"
                        value={formData.linkedinCompany}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Facebook Page</label>
                      <input 
                        type="url" 
                        name="facebook" 
                        placeholder="https://www.facebook.com/yourcompany"
                        value={formData.facebook}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Twitter/X Handle</label>
                      <input 
                        type="url" 
                        name="twitter" 
                        placeholder="https://twitter.com/yourcompany"
                        value={formData.twitter}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Instagram</label>
                      <input 
                        type="url" 
                        name="instagram" 
                        placeholder="https://www.instagram.com/yourcompany"
                        value={formData.instagram}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Additional Professional Links</label>
                    <div style={{ marginTop: '8px' }}>
                      <div className="form-row-comprehensive">
                        <div className="form-group-comprehensive">
                          <input 
                            type="text" 
                            placeholder="Link type (e.g., Glassdoor, Indeed)"
                            value={linkTypeInput}
                            onChange={(e) => setLinkTypeInput(e.target.value)}
                          />
                        </div>
                        <div className="form-group-comprehensive">
                          <input 
                            type="url" 
                            placeholder="https://example.com"
                            value={linkUrlInput}
                            onChange={(e) => setLinkUrlInput(e.target.value)}
                          />
                        </div>
                      </div>
                      <button type="button" className="add-btn" onClick={addLink}>
                        <FontAwesomeIcon icon={faPlus} /> Add Link
                      </button>
                    </div>
                    
                    <div className="links-container">
                      {additionalLinks.map((link, index) => (
                        <div key={index} className="link-item">
                          <div className="link-item-content">
                            <div className="link-item-type">
                              <FontAwesomeIcon icon={faLink} /> {link.type}
                            </div>
                            <div className="link-item-url">
                              <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
                            </div>
                          </div>
                          <button type="button" className="remove-item-btn" onClick={() => removeLink(index)}>
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Terms Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faClipboardCheck} />
                    Terms & Additional Information
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>How did you hear about us?</label>
                    <select 
                      name="referralSource"
                      value={formData.referralSource}
                      onChange={handleInputChange}
                    >
                      <option value="">Select source</option>
                      <option value="search">Search Engine (Google, Bing, etc.)</option>
                      <option value="social">Social Media</option>
                      <option value="referral">Referral from colleague</option>
                      <option value="advertisement">Advertisement</option>
                      <option value="event">Industry Event/Conference</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Additional Comments or Questions</label>
                    <textarea 
                      name="additionalComments" 
                      placeholder="Share any additional information, special requirements, or questions you have about our platform"
                      value={formData.additionalComments}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Would you like to participate in any volunteering?</label>
                    <select
                      name="participateInVolunteering"
                      value={formData.participateInVolunteering}
                      onChange={handleInputChange}
                    >
                      <option value="">Select an option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  {formData.participateInVolunteering === 'yes' && (
                    <div className="form-group-comprehensive">
                      <label>Volunteering Details</label>
                      <textarea 
                        name="volunteeringDetails" 
                        placeholder="Please describe the volunteering activities you'd like to participate in"
                        value={formData.volunteeringDetails}
                        onChange={handleInputChange}
                        rows="4"
                      />
                    </div>
                  )}

                  <div className="checkbox-group">
                    <input 
                      type="checkbox" 
                      id="agreeTerms" 
                      name="agreeTerms" 
                      required
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="agreeTerms">I agree to the Terms of Service and Privacy Policy <span className="required">*</span></label>
                  </div>

                  <div className="checkbox-group">
                    <input 
                      type="checkbox" 
                      id="agreeDataProcessing" 
                      name="agreeDataProcessing" 
                      required
                      checked={formData.agreeDataProcessing}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="agreeDataProcessing">I consent to the processing of company data for recruitment purposes <span className="required">*</span></label>
                  </div>

                  <div className="checkbox-group">
                    <input 
                      type="checkbox" 
                      id="agreeMarketing" 
                      name="agreeMarketing"
                      checked={formData.agreeMarketing}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="agreeMarketing">I agree to receive marketing communications and platform updates</label>
                  </div>

                  <div className="checkbox-group">
                    <input 
                      type="checkbox" 
                      id="verifyInfo" 
                      name="verifyInfo" 
                      required
                      checked={formData.verifyInfo}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="verifyInfo">I verify that all information provided is accurate and truthful <span className="required">*</span></label>
                  </div>

                  {/* Admin Approval Message */}
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      style={{
                        color: '#1976d2',
                        fontSize: '18px',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1976d2',
                        marginBottom: '6px'
                      }}>
                        Account Approval Process
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#424242',
                        lineHeight: '1.6'
                      }}>
                        Complete your profile <strong>80%</strong>. If it is <strong>80% completed</strong>, it will be reviewed and then approved by our admin team within <strong>24 hours</strong>. You will receive an email notification once your account is approved. Please check your <strong>spam folder</strong> if you don't see the email in your inbox.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="submit-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {submitError && (
                    <div className="error-message" style={{ marginBottom: '12px', color: '#e74c3c', textAlign: 'center' }}>
                      {submitError}
                    </div>
                  )}
                  <button type="submit" className="submit-btn" disabled={isLoading} style={{ margin: '0 auto' }}>
                    <FontAwesomeIcon icon={faCheckCircle} /> 
                    {isLoading ? 'Submitting...' : 'Create Employer Profile'}
                  </button>
                  <p style={{ marginTop: '8px', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                    Your account will be reviewed and activated within 24-48 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployerRegistrationForm;

